import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye } from "lucide-react";
import api, { money, formatApiError, fileUrl } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import AdminLayout from "@/components/admin/AdminLayout";
import { TableShell, Th, Td, EmptyRow } from "@/components/admin/DataTable";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["New", "Under Review", "Call Pending", "Verified", "Objection", "Approved", "Rejected", "Cancelled"];

export default function Requests() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [objection, setObjection] = useState("");
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertData, setConvertData] = useState({ branch_id: "", assigned_to: "", advance_received: false });

  const load = () => {
    const qs = new URLSearchParams();
    if (filter) qs.set("status", filter);
    if (search) qs.set("search", search);
    api.get(`/requests?${qs}`).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter, search]);
  useEffect(() => {
    api.get("/users").then((r) => setStaff(r.data.filter((u) => u.role !== "customer"))).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const patch = async (id, body) => {
    try { await api.put(`/requests/${id}`, body); toast.success("Updated"); load(); if (active) { const r = await api.get(`/requests?search=${active.request_no}`); setActive(r.data.find((x) => x.id === id) || active); } }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doConvert = async () => {
    try {
      const r = await api.post(`/requests/${active.id}/convert`, convertData);
      toast.success(`Converted to ${r.data.order_no}`);
      setConvertOpen(false); setActive(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delFile = async (fid) => {
    try { await api.delete(`/files/${fid}`); toast.success("File deleted"); if (active) { const r = await api.get(`/requests?search=${active.request_no}`); setActive(r.data.find((x) => x.id === active.id) || active); } }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Quote Requests">
      <div className="mb-4 flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${!filter ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>All</button>
        {STATUSES.map((s) => <button key={s} onClick={() => setFilter(s)} data-testid={`req-filter-${s}`} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${filter === s ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{s}</button>)}
      </div>
      <TableShell search={search} onSearch={setSearch} searchPlaceholder="Search request no, name, mobile...">
        <thead className="border-b border-border bg-secondary/40"><tr><Th>Request</Th><Th>Customer</Th><Th>Product</Th><Th>Date &amp; Time</Th><Th>Monthly</Th><Th>Status</Th><Th className="text-right">Action</Th></tr></thead>
        <tbody className="divide-y divide-border">
          {items.length === 0 ? <EmptyRow colSpan={7} /> : items.map((r) => (
            <tr key={r.id} className="hover:bg-secondary/30" data-testid={`req-row-${r.id}`}>
              <Td className="font-mono text-xs">{r.request_no}</Td>
              <Td><p className="font-medium">{r.customer_name}</p><p className="text-xs text-muted-foreground">{r.mobile}</p></Td>
              <Td>{r.product_name}</Td>
              <Td className="text-xs text-muted-foreground" data-testid={`req-date-${r.id}`}>{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</Td>
              <Td className="font-semibold">{money(r.monthly_installment)}</Td>
              <Td><StatusBadge status={r.status} /></Td>
              <Td className="text-right"><Button size="sm" variant="outline" onClick={() => { setActive(r); }} data-testid={`view-req-${r.id}`}><Eye className="mr-1 h-3.5 w-3.5" />View</Button></Td>
            </tr>
          ))}
        </tbody>
      </TableShell>

      <Dialog open={!!active && !convertOpen} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-3">{active.request_no}<StatusBadge status={active.status} /></DialogTitle></DialogHeader>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-4 text-sm">
                  <div><p className="text-muted-foreground">Customer</p><p className="font-medium">{active.customer_name}</p></div>
                  <div><p className="text-muted-foreground">Mobile</p><p className="font-medium">{active.mobile}</p></div>
                  <div><p className="text-muted-foreground">CNIC</p><p className="font-medium">{active.cnic}</p></div>
                  <div><p className="text-muted-foreground">City</p><p className="font-medium">{active.city}</p></div>
                  <div className="col-span-2"><p className="text-muted-foreground">Address</p><p className="font-medium">{active.address}</p></div>
                  <div><p className="text-muted-foreground">Income</p><p className="font-medium">{active.monthly_income || "—"}</p></div>
                  <div><p className="text-muted-foreground">Occupation</p><p className="font-medium">{active.occupation || "—"}</p></div>
                  <div><p className="text-muted-foreground">Product</p><p className="font-medium">{active.product_name}</p></div>
                  <div><p className="text-muted-foreground">Plan</p><p className="font-medium">{active.plan_name}</p></div>
                  <div><p className="text-muted-foreground">Advance</p><p className="font-medium">{money(active.advance_payment)}</p></div>
                  <div><p className="text-muted-foreground">Monthly</p><p className="font-medium text-primary">{money(active.monthly_installment)}</p></div>
                  <div className="col-span-2"><p className="text-muted-foreground">Requested On</p><p className="font-medium" data-testid="req-detail-date">{active.created_at ? new Date(active.created_at).toLocaleString() : "—"}</p></div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {active.photo_id && <span className="inline-flex items-center gap-1 rounded-md border border-border pl-3 text-xs"><a href={fileUrl(active.photo_id)} target="_blank" rel="noreferrer" className="py-1.5 hover:text-primary">Customer Photo</a><button onClick={() => delFile(active.photo_id)} data-testid="del-photo" className="border-l border-border px-2 py-1.5 text-destructive hover:bg-destructive/10">✕</button></span>}
                  {active.cnic_front_id && <span className="inline-flex items-center gap-1 rounded-md border border-border pl-3 text-xs"><a href={fileUrl(active.cnic_front_id)} target="_blank" rel="noreferrer" className="py-1.5 hover:text-primary">CNIC Front</a><button onClick={() => delFile(active.cnic_front_id)} data-testid="del-cnic-front" className="border-l border-border px-2 py-1.5 text-destructive hover:bg-destructive/10">✕</button></span>}
                  {active.cnic_back_id && <span className="inline-flex items-center gap-1 rounded-md border border-border pl-3 text-xs"><a href={fileUrl(active.cnic_back_id)} target="_blank" rel="noreferrer" className="py-1.5 hover:text-primary">CNIC Back</a><button onClick={() => delFile(active.cnic_back_id)} data-testid="del-cnic-back" className="border-l border-border px-2 py-1.5 text-destructive hover:bg-destructive/10">✕</button></span>}
                </div>

                {(active.staff_notes || []).length > 0 && (
                  <div className="grid gap-1.5">{active.staff_notes.map((n, i) => <p key={i} className={`rounded-lg px-3 py-2 text-sm ${n.type === "objection" ? "bg-orange-500/10 text-orange-700 dark:text-orange-400" : "bg-secondary/60"}`}><span className="font-semibold">{n.by}:</span> {n.text}</p>)}</div>
                )}

                {can("requests", "edit") && (
                  <div className="grid gap-3 rounded-lg border border-border p-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="mb-1.5 block text-xs">Status</Label>
                        <Select value={active.status} onValueChange={(v) => patch(active.id, { status: v })}>
                          <SelectTrigger data-testid="req-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="mb-1.5 block text-xs">Assign Staff</Label>
                        <Select value={active.assigned_to || ""} onValueChange={(v) => patch(active.id, { assigned_to: v })}>
                          <SelectTrigger data-testid="req-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                          <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Input value={objection} onChange={(e) => setObjection(e.target.value)} placeholder="Add note / objection..." data-testid="req-note-input" />
                      <Button variant="outline" onClick={() => { patch(active.id, { note: objection }); setObjection(""); }} data-testid="req-add-note">Note</Button>
                      <Button variant="outline" onClick={() => { patch(active.id, { objection, status: "Objection" }); setObjection(""); }} data-testid="req-add-objection">Objection</Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {can("requests", "approve") && <Button onClick={() => patch(active.id, { status: "Approved" })} data-testid="req-approve" className="bg-emerald-600 hover:bg-emerald-700">Approve</Button>}
                      {can("requests", "approve") && <Button variant="destructive" onClick={() => patch(active.id, { status: "Rejected" })} data-testid="req-reject">Reject</Button>}
                      {can("orders", "create") && active.status !== "Converted" && <Button onClick={() => { setConvertData({ branch_id: active.branch_id || "", assigned_to: active.assigned_to || "", advance_received: false }); setConvertOpen(true); }} data-testid="req-convert" className="ml-auto">Convert to Order</Button>}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Convert to Order</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div><Label className="mb-1.5 block">Branch</Label>
              <Select value={convertData.branch_id} onValueChange={(v) => setConvertData({ ...convertData, branch_id: v })}>
                <SelectTrigger data-testid="convert-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block">Assign Staff</Label>
              <Select value={convertData.assigned_to} onValueChange={(v) => setConvertData({ ...convertData, assigned_to: v })}>
                <SelectTrigger data-testid="convert-staff"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={convertData.advance_received} onChange={(e) => setConvertData({ ...convertData, advance_received: e.target.checked })} data-testid="convert-advance-received" />Advance / down payment already received</label>
            <p className="text-xs text-muted-foreground">Creates an order and generates the installment schedule. If the down payment is not yet received, the order shows <span className="font-semibold">Pending Down Payment</span> to the customer until they upload a receipt and you confirm it.</p>
            <Button onClick={doConvert} className="rounded-full" data-testid="confirm-convert">Confirm & Create Order</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
